import React, { useState, useEffect, forwardRef } from "react";
import { cn } from "@/utils";

interface DriversLicenseInputProps {
  value: string;
  onChange: (value: string) => void;
  onBlurValidate?: (value: string) => void;
  onBlur?: () => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
}

// Русские буквы, которые могут быть в серии ВУ
const RUSSIAN_LETTERS = "АБВГДЕЖЗИЙКЛМНОПРСТУФХЦЧШЩЪЫЬЭЮЯ";

/**
 * Компонент ввода водительского удостоверения.
 * Формат: XX YY NNNNNN
 * где XX - 2 цифры (код региона), YY - 2 символа (цифры или русские буквы), NNNNNN - 6 цифр номера
 */
const DriversLicenseInput = forwardRef<HTMLInputElement, DriversLicenseInputProps>(
  ({ value, onChange, onBlurValidate, onBlur, placeholder = "00 00 000000", className, disabled = false }, ref) => {
    const [displayValue, setDisplayValue] = useState("");
    const [error, setError] = useState<string | null>(null);

    /**
     * Форматирует строку ВУ в правильный формат: XX YY NNNNNN
     * Первые 2 символа - цифры (код региона)
     * Следующие 2 - цифры или русские буквы
     * Последние 6 - цифры номера
     */
    const formatDriversLicense = (input: string): string => {
      // Приводим к верхнему регистру для русских букв
      const upperInput = input.toUpperCase();

      // Разрешаем только цифры и русские буквы (убираем всё остальное включая пробелы)
      const allowedChars = new RegExp(`[^\\d${RUSSIAN_LETTERS}]`, "g");
      const chars = upperInput.replace(allowedChars, "");

      let formatted = "";

      for (let i = 0; i < chars.length && i < 10; i++) {
        // Добавляем пробел после 2 символов (код региона)
        if (i === 2) {
          formatted += " ";
        }
        // Добавляем пробел после 4 символов (вся серия)
        if (i === 4) {
          formatted += " ";
        }

        const char = chars[i];

        // Первые 2 символа - только цифры (код региона)
        if (i < 2) {
          if (/\d/.test(char)) {
            formatted += char;
          }
        }
        // Символы 2-3 - цифры или русские буквы
        else if (i < 4) {
          if (/\d/.test(char) || RUSSIAN_LETTERS.includes(char)) {
            formatted += char;
          }
        }
        // Номер (позиции 4-9) - только цифры
        else {
          if (/\d/.test(char)) {
            formatted += char;
          }
        }
      }

      return formatted;
    };

    useEffect(() => {
      if (value) {
        // Всегда форматируем входящее значение для корректного отображения
        const formatted = formatDriversLicense(value);
        setDisplayValue(formatted);
      } else {
        setDisplayValue("");
      }
    }, [value]);

    const validateDriversLicense = (dlStr: string): string | null => {
      // Формат: "XX YY NNNNNN" = 12 символов (2 + 1 + 2 + 1 + 6)
      if (!dlStr || dlStr.length < 12) {
        return null;
      }

      const parts = dlStr.split(" ");
      if (parts.length !== 3) {
        return "Неверный формат";
      }

      const [regionCode, seriesSuffix, number] = parts;

      if (regionCode.length !== 2) {
        return "Код региона должен содержать 2 цифры";
      }

      if (seriesSuffix.length !== 2) {
        return "Серия должна содержать 2 символа";
      }

      if (number.length !== 6) {
        return "Номер должен содержать 6 цифр";
      }

      // Проверяем код региона: только цифры
      if (!/^\d{2}$/.test(regionCode)) {
        return "Код региона должен содержать только цифры";
      }

      // Проверяем серию: цифры или русские буквы
      const seriesPattern = new RegExp(`^[\\d${RUSSIAN_LETTERS}]{2}$`);
      if (!seriesPattern.test(seriesSuffix)) {
        return "Неверный формат серии";
      }

      // Проверяем номер: только цифры
      if (!/^\d{6}$/.test(number)) {
        return "Номер должен содержать только цифры";
      }

      return null;
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const input = e.target.value;
      const formatted = formatDriversLicense(input);

      setDisplayValue(formatted);
      onChange(formatted);

      // Валидация при полном вводе (12 символов: XX YY NNNNNN)
      if (formatted.length === 12) {
        const validationError = validateDriversLicense(formatted);
        setError(validationError);
      } else {
        setError(null);
      }
    };

    const handleBlur = () => {
      if (displayValue && displayValue.length > 0 && displayValue.length < 12) {
        setError("Введите полные данные ВУ");
      } else if (displayValue.length === 12 && !error && onBlurValidate) {
        onBlurValidate(displayValue);
      }
      onBlur?.();
    };

    return (
      <div className="w-full">
        <input
          ref={ref}
          type="text"
          value={displayValue}
          onChange={handleChange}
          onBlur={handleBlur}
          placeholder={placeholder}
          disabled={disabled}
          maxLength={12}
          className={cn(
            "block w-full px-3 py-2 border rounded-lg bg-surface-elevated text-fs-sm text-ink-1 placeholder:text-ink-4",
            "transition-colors focus:outline-none focus:border-accent focus:ring-2 focus:ring-accent/30",
            error
              ? "border-red-400 focus:border-red-500 focus:ring-red-500/30 bg-red-50/50 dark:bg-red-900/10"
              : "border-line",
            className,
          )}
        />
        {error && <p className="mt-1 text-xs text-error-600 dark:text-error-400">{error}</p>}
      </div>
    );
  },
);

DriversLicenseInput.displayName = "DriversLicenseInput";

export default DriversLicenseInput;
